/**
 * @fileoverview Shared import run-flow state machine (upload → parse → map →
 *               match-key → dry-run → commit) for the UnifiedImportWizard.
 * @module import-editor/runFlow/useImportRunFlow
 *
 * Responsibilities:
 * - Own the entire run-flow wizard state and every transition handler:
 *   template selection/hydration, file pick/reset, upload, async preview
 *   parse, sheet switching, dry-run + cancel + re-run, commit, and the
 *   post-commit save-as-variant / save-as-new-template actions.
 * - Gate host-specific behavior (manual mode picker, spatial filter,
 *   draft-run persistence, preview-session persistence) on explicit
 *   options and on the optional capabilities of the injected services
 *   adapter — never on host identity.
 *
 * Dependencies:
 * - `../unifiedWizard/services` — the host-injected `ImportWizardServices`
 *   adapter contract (all I/O goes through it; this hook calls no API and
 *   no Dexie table directly).
 * - `../unifiedWizard/templateSelection` — template → state projection.
 * - `../utils/geoFileDetect` — geo file / GeoParquet detection.
 * - `./navigation` — canonical run-step list + lookup-mode skip.
 *
 * Architecture:
 * - This hook IS the run-flow state machine; `UnifiedImportRunFlow` is a
 *   pure composition layer over it. Both legacy host containers
 *   (tenant-web ImportWizardContainer, system-web SystemImportWizardContainer)
 *   were retired in favor of this hook (Phase 2A, G-002).
 * - Upload/parse/dry-run/commit are documented online-only exceptions to
 *   the local-first rule; draft-run persistence and preview sessions are
 *   mediated by the host adapter's local-first services.
 *
 * Notes:
 * - Draft-run persistence (system-web) uses a launch-scoped sessionStorage
 *   resume key supplied by the host — a reviewed exception so a page reload
 *   can resume an in-flight parse without leaking run state across launches.
 * - `dryRunSummary` follows the April 2026 taxonomy (`excluded.*` /
 *   `failedByKind.*`); no legacy `skipped` / `failed` / `outOfBounds` fields.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ImportTemplate,
  ImportTemplateListItem,
  ImportTemplateSampleBlob,
  ImportTemplateSamplePreview,
  ImportRun,
} from '@firesquirrel/local-db';
import type { ProgressSummary } from '../components/ImportProgressIndicator';
import type { ImportWizardServices, RunPollProgress, ParsedFilePreview, AvailableImportFile } from '../unifiedWizard/services';
import { buildTemplateSelectionPatch, toSelectedTemplate } from '../unifiedWizard/templateSelection';
import { isGeoFile } from '../utils/geoFileDetect';
import { getVisibleRunSteps, type RunStepId } from './navigation';
import {
  EMPTY_RUN_FLOW_STATE,
  RUN_FLOW_LIVE_PROGRESS_EMPTY,
  type ImportRunFlowState,
  type RunFlowDryRunRow,
  type RunFlowDryRunSummary,
  type RunFlowLiveProgress,
} from './runFlowTypes';

// ---------------------------------------------------------------------------
// Options / result contracts
// ---------------------------------------------------------------------------

/**
 * @contract UseImportRunFlowOptions
 *
 * Purpose:
 * - Configure the shared run-flow state machine for a host.
 *
 * Fields:
 * - services: host-injected adapter implementing ImportWizardServices.
 * - initialStepId/initialTemplateId: deep-link entry points.
 * - initialFile/initialSampleBlob/initialSamplePreview: Card 3
 *   save-and-import handoff so the user is not asked to re-upload.
 * - enableManualModePicker: tenant-web geo autodetect + parcel/lookup picker.
 * - restorePreviewSession: tenant-web resume of the Dexie preview session.
 * - draftRunPersistence: system-web local-first draft run + launch-scoped
 *   resume (sessionStorage key supplied by the host page).
 *
 * Rules:
 * - All I/O flows through `services`; the hook performs no direct fetch.
 */
export interface UseImportRunFlowOptions {
  services: ImportWizardServices;
  initialStepId?: RunStepId;
  initialTemplateId?: string;
  initialFile?: File | null;
  initialSampleBlob?: ImportTemplateSampleBlob;
  initialSamplePreview?: ImportTemplateSamplePreview;
  enableManualModePicker?: boolean;
  restorePreviewSession?: boolean;
  draftRunPersistence?: {
    /** Launch-scoped sessionStorage key holding the resumable draft run id. */
    resumeStorageKey: string;
  } | null;
}

export type { ImportRunFlowState } from './runFlowTypes';

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/**
 * @process isRecoverableCreateImportRunError
 *
 * Steps:
 * 1. Normalize the create-run error into a message.
 * 2. Treat proxy abort and timeout messages as recoverable because the API
 *    may still accept and process the run.
 *
 * Inputs:
 * - err: unknown error thrown by createImportRun / commitImportRun.
 *
 * Outputs:
 * - Whether the flow should continue by polling the known run id.
 *
 * Failure Handling:
 * - Non-timeout errors remain fatal and surface to the user.
 *
 * Idempotency:
 * - REQUIRED.
 */
export function isRecoverableCreateImportRunError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? '');
  return /abort|timed out|timeout/i.test(message);
}

/**
 * @process getProgressSummaryTotals
 *
 * Steps:
 * 1. Read progress summary counters from an import-run summary projection.
 * 2. Default missing nested failure buckets to zero for partial runs.
 *
 * Inputs:
 * - summary: optional summary from dry-run/commit polling.
 *
 * Outputs:
 * - excludedTotal, failedTotal, and rowsProcessed counters for UI gating.
 *
 * Failure Handling:
 * - Missing or partial summary buckets render as zero instead of throwing.
 *
 * Idempotency:
 * - REQUIRED.
 */
export function getProgressSummaryTotals(summary: Partial<ProgressSummary> | null | undefined): {
  excludedTotal: number;
  failedTotal: number;
  rowsProcessed: number;
} {
  const created = summary?.created ?? 0;
  const updated = summary?.updated ?? 0;
  const excludedTotal =
    (summary?.excluded?.outOfJurisdiction ?? 0) +
    (summary?.excluded?.missingCoordinates ?? 0) +
    (summary?.excluded?.duplicateInsertOnly ?? 0) +
    (summary?.excluded?.missingMatchKey ?? 0) +
    (summary?.excluded?.noMatch ?? 0) +
    (summary?.excluded?.staleSource ?? 0);
  const failedTotal =
    (summary?.failedByKind?.transform ?? 0) +
    (summary?.failedByKind?.entityBuild ?? 0);
  return {
    excludedTotal,
    failedTotal,
    rowsProcessed: created + updated + excludedTotal + failedTotal,
  };
}

/**
 * @process deriveRunUiPhase
 *
 * Steps:
 * 1. Map the coarse poller phase + raw backend status onto the progress
 *    indicator's fine-grained phase vocabulary for the active run phase.
 * 2. For commits, promote to 'finalizing' when every row is accounted for
 *    even if the backend status still reads 'committing' (race guard).
 *
 * Idempotency:
 * - REQUIRED — pure function.
 */
export function deriveRunUiPhase(
  progress: RunPollProgress,
  runPhase: 'dryRun' | 'commit',
): RunFlowLiveProgress['phase'] {
  if (progress.phase === 'queued') return 'queued';
  if (progress.phase === 'failed') return 'failed';
  if (progress.phase === 'cancelled') return 'cancelled';
  const raw = progress.rawStatus;
  if (progress.phase === 'done') return runPhase === 'commit' ? 'done' : 'finalizing';
  // phase === 'running'
  if (runPhase === 'dryRun') {
    return raw === 'awaiting-commit' || raw === 'completed' ? 'finalizing' : 'dry-run';
  }
  const { rowsProcessed } = getProgressSummaryTotals(progress.summary ?? undefined);
  const allRowsDone =
    progress.summary != null
    && progress.summary.totalRows > 0
    && rowsProcessed >= progress.summary.totalRows;
  return raw === 'finalizing' || allRowsDone ? 'finalizing' : 'commit';
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @hook useImportRunFlow
 *
 * Purpose:
 * - Own the run-flow wizard state machine for `UnifiedImportRunFlow`.
 *
 * Returns:
 * - `state` + `patch`, template list, per-operation busy/error flags, the
 *   live progress snapshot, step navigation (visibleSteps, activeStepId,
 *   goNext/goBack, canAdvance, nextLabel), and every action handler.
 *
 * Side Effects:
 * - On mount: loads the runnable template list, seeds the Card 3 handoff
 *   file/preview, restores a draft run (draftRunPersistence) or preview
 *   session (restorePreviewSession) when enabled.
 * - During parse/dry-run/commit: calls adapter methods that perform online
 *   I/O (documented online-only exception) and local-first persistence.
 *
 * Offline Behavior:
 * - Template list reads are Dexie-first via the adapter; upload/parse/
 *   dry-run/commit require connectivity and surface errors inline.
 */
export function useImportRunFlow(options: UseImportRunFlowOptions) {
  const {
    services,
    initialStepId,
    initialTemplateId,
    initialFile,
    initialSampleBlob,
    initialSamplePreview,
    enableManualModePicker = false,
    restorePreviewSession = false,
    draftRunPersistence = null,
  } = options;

  const [state, setState] = useState<ImportRunFlowState>(() => ({
    ...EMPTY_RUN_FLOW_STATE,
    // Hosts without the manual picker (system-web) always have an
    // authoritative dataMode: the selected template's, or 'parcel' by
    // default. Hosts with the picker (tenant-web) start at null so the
    // user chooses explicitly for non-geo files.
    dataMode: enableManualModePicker ? null : 'parcel',
  }));
  const [activeStepId, setActiveStepId] = useState<RunStepId>(initialStepId ?? 'upload');
  const [templates, setTemplates] = useState<ImportTemplateListItem[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewUiStage, setPreviewUiStage] = useState<
    'idle' | 'uploading' | 'queued' | 'processing' | 'parsed' | 'error'
  >('idle');
  const parseRequestIdRef = useRef(0);
  // Stale-closure guard for the async preview path. Each parse/upload
  // captures the current token; when the user picks a different file the
  // token bumps and any in-flight callback bails before mutating state.
  const activePreviewTokenRef = useRef(0);
  const [dryRunning, setDryRunning] = useState(false);
  const [dryRunError, setDryRunError] = useState<string | null>(null);
  // Tracks whether a cancel request is in flight. The backend picks up the
  // cancel signal at the next 500-row checkpoint; until the poller returns
  // a `cancelled` status we keep the spinner + disable Cancel so we don't
  // fire duplicate cancels.
  const [cancellingDryRun, setCancellingDryRun] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [liveProgress, setLiveProgress] = useState<RunFlowLiveProgress>(RUN_FLOW_LIVE_PROGRESS_EMPTY);

  // Save-as-variant state. The card shows after a shell-based commit and is
  // hidden once the user picks any option so we don't keep prompting.
  const [saveVariantBusy, setSaveVariantBusy] = useState(false);
  const [saveVariantError, setSaveVariantError] = useState<string | null>(null);
  const [saveVariantDismissed, setSaveVariantDismissed] = useState(false);

  // Save-as-new-template state (gold-star flow). Appears on the commit step
  // when the user ran an import WITHOUT selecting a template.
  const [saveAsTemplateBusy, setSaveAsTemplateBusy] = useState(false);
  const [saveAsTemplateError, setSaveAsTemplateError] = useState<string | null>(null);
  const [saveAsTemplateDismissed, setSaveAsTemplateDismissed] = useState(false);

  const patch = useCallback(
    (p: Partial<ImportRunFlowState>) => setState(prev => ({ ...prev, ...p })),
    [],
  );

  const visibleSteps = getVisibleRunSteps(state.dataMode);
  const currentIndex = Math.max(0, visibleSteps.findIndex(s => s.id === activeStepId));
  const canGoBack = currentIndex > 0;
  const canGoNext = currentIndex < visibleSteps.length - 1;

  // -----------------------------------------------------------------------
  // Mount: seed from the Card 3 save-and-import handoff (one-shot)
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!initialFile && !initialSampleBlob && !initialSamplePreview) return;
    setState(prev => ({
      ...prev,
      file: initialFile ?? prev.file,
      filename: initialFile?.name ?? initialSampleBlob?.filename ?? prev.filename,
      fileSize: initialFile?.size ?? initialSampleBlob?.fileSize ?? prev.fileSize,
      uploadResult: initialSampleBlob
        ? {
            runId: prev.uploadResult?.runId ?? prev.runId ?? '',
            blobUrl: initialSampleBlob.blobUrl,
            fileType: initialSamplePreview?.fileType,
          }
        : prev.uploadResult,
      parseResult: initialSamplePreview
        ? {
            fileType: initialSamplePreview.fileType,
            columns: initialSamplePreview.columns,
            sampleRows: initialSamplePreview.sampleRows,
            totalRows: initialSamplePreview.totalRows,
            sheets: initialSamplePreview.availableSheets,
            activeSheet: initialSamplePreview.activeSheet,
            hasGeometry: prev.parseResult?.hasGeometry ?? false,
          }
        : prev.parseResult,
      selectedSheet: initialSamplePreview?.activeSheet ?? prev.selectedSheet,
    }));
    // One-shot seed — the navigation state is a hand-off event, not an
    // ongoing subscription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------------------------------------------------
  // Mount: load runnable templates + hydrate the deep-linked template
  // -----------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await services.listImportTemplates();
        // Run-flow picker: never show drafts. A draft is in-progress
        // authoring work and is not a valid template to run against.
        const runnable = data.filter(t => !t.isDraft && t.status !== 'draft');
        if (cancelled) return;
        setTemplates(runnable);

        if (initialTemplateId) {
          const listed = runnable.find(x => x._id === initialTemplateId);
          const full = await services.getImportTemplate(initialTemplateId);
          if (cancelled || !full || full.isDraft || full.status === 'draft') return;
          patch(buildTemplateSelectionPatch(full, toSelectedTemplate(full, listed)));
        }
      } catch {
        if (!cancelled) setTemplates([]);
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    })();
    return () => { cancelled = true; };
  }, [initialTemplateId, services, patch]);

  // -----------------------------------------------------------------------
  // Mount: draft-run resume / creation (system-web draft persistence)
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!draftRunPersistence || !services.createDraftImportRun) return;
    const { resumeStorageKey } = draftRunPersistence;
    let cancelled = false;

    (async () => {
      const persistedRunId =
        typeof window !== 'undefined' && window.sessionStorage
          ? window.sessionStorage.getItem(resumeStorageKey)
          : null;
      let run: ImportRun | null = null;

      if (persistedRunId && services.getImportRun) {
        try {
          run = await services.getImportRun(persistedRunId);
        } catch {
          run = null;
        }
      }

      if (!run) {
        run = await services.createDraftImportRun!({
          templateId: initialTemplateId,
          filename: initialFile?.name ?? initialSampleBlob?.filename,
          fileSize: initialFile?.size ?? initialSampleBlob?.fileSize,
          fileType: initialFile?.type || initialSamplePreview?.fileType || undefined,
          blobUrl: initialSampleBlob?.blobUrl,
          selectedSheet: initialSamplePreview?.activeSheet ?? null,
          parseResult: initialSamplePreview
            ? {
                fileType: initialSamplePreview.fileType,
                columns: initialSamplePreview.columns,
                sampleRows: initialSamplePreview.sampleRows,
                totalRows: initialSamplePreview.totalRows,
                sheets: initialSamplePreview.availableSheets,
                activeSheet: initialSamplePreview.activeSheet,
                hasGeometry: false,
              }
            : null,
        });
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.setItem(resumeStorageKey, run._id);
        }
      }

      if (cancelled) return;
      // Hydrate the wizard from the draft run record.
      patch({
        runId: run._id,
        importName: run.importName ?? '',
        importDescription: run.importDescription ?? '',
        filename: run.filename,
        fileSize: run.fileSize ?? null,
        uploadResult: run.blobUrl ? { runId: run._id, blobUrl: run.blobUrl ?? null } : null,
        parseResult: (run.parseResult as ParsedFilePreview | undefined) ?? null,
        selectedSheet: run.selectedSheet ?? run.parseResult?.activeSheet ?? null,
      });

      // Resume an interrupted parse: the draft still points at an in-flight
      // preview job, so poll it to completion and fold the result back in.
      if (run.status === 'parsing' && run.previewJobId) {
        setParsing(true);
        setParseError(null);
        try {
          const resumed = await services.pollPreviewJob(run.previewJobId);
          if (cancelled) return;
          patch({ parseResult: resumed, selectedSheet: resumed.activeSheet ?? null });
          await services.updateDraftImportRun?.(run._id, {
            status: 'parsed',
            parseResult: resumed,
            selectedSheet: resumed.activeSheet ?? null,
            errorMessage: null,
          });
        } catch (err: any) {
          if (!cancelled) setParseError(err.message ?? 'Parse failed');
        } finally {
          if (!cancelled) setParsing(false);
        }
      }
    })().catch((err) => {
      console.warn('[ImportRunFlow] Failed to initialize draft run:', err);
    });

    return () => { cancelled = true; };
    // One-shot per launch — the resume key is stable for the wizard's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------------------------------------------------
  // Mount: preview-session restore (tenant-web)
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!restorePreviewSession || !services.getImportPreviewSession) return;
    let cancelled = false;
    services.getImportPreviewSession()
      .then(session => {
        if (cancelled || !session || session.status !== 'succeeded' || !session.parseResult) return;
        setState(prev => ({
          ...prev,
          filename: session.filename,
          fileSize: session.fileSize ?? null,
          uploadResult: {
            runId: session.runId ?? prev.uploadResult?.runId ?? '',
            blobUrl: session.blobUrl,
            fileType: session.fileType ?? session.parseResult?.fileType,
          },
          parseResult: session.parseResult ?? null,
          selectedSheet: session.selectedSheet ?? session.parseResult?.activeSheet ?? null,
          dataMode: isGeoFile(session.filename) ? 'parcel' : prev.dataMode,
        }));
      })
      .catch((err) => {
        console.error('[ImportRunFlow] Failed to restore preview session:', err);
      });
    return () => { cancelled = true; };
  }, [restorePreviewSession, services]);

  // -----------------------------------------------------------------------
  // Draft-run auto-sync (system-web): mirror wizard state onto the draft
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!draftRunPersistence || !state.runId || !services.updateDraftImportRun) return;
    void services.updateDraftImportRun(state.runId, {
      templateId: state.selectedTemplate?._id,
      importName: state.importName || undefined,
      importDescription: state.importDescription || undefined,
      filename: state.filename || undefined,
      fileSize: state.fileSize ?? undefined,
      selectedSheet: state.selectedSheet,
      fieldMappings: state.fieldMappings,
      importBehavior: state.importBehavior,
      matchKeyField: state.fieldMappings.find((mapping) => mapping.isMatchKey)?.targetField,
      boundaryCheckEnabled: state.boundaryCheckEnabled,
      parseResult: state.parseResult,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.runId,
    state.selectedTemplate?._id,
    state.importName,
    state.importDescription,
    state.filename,
    state.fileSize,
    state.selectedSheet,
    state.fieldMappings,
    state.importBehavior,
    state.boundaryCheckEnabled,
    state.parseResult,
  ]);

  // -----------------------------------------------------------------------
  // Template selection
  // -----------------------------------------------------------------------
  const handleTemplateSelect = useCallback(async (t: ImportTemplateListItem | null) => {
    // Deselect: clear template-derived state. With the manual picker, a
    // non-geo file (or no file) resets dataMode to null so the picker
    // reappears; a geo file still forces 'parcel'. Without the picker,
    // dataMode falls back to the 'parcel' default.
    const deselectMode = enableManualModePicker
      ? (state.file && isGeoFile(state.file.name) ? 'parcel' : null)
      : 'parcel';
    patch({
      selectedTemplate: t,
      fieldMappings: [],
      importBehavior: t?.importBehavior ?? 'upsert',
      dataMode: (t ? ((t.dataMode ?? 'parcel') as ImportRunFlowState['dataMode']) : deselectMode),
      templateBoundaryCheckDefault: false,
      boundaryCheckEnabled: false,
    });
    if (t?._id) {
      try {
        const full = await services.getImportTemplate(t._id);
        if (full && !full.isDraft && full.status !== 'draft') {
          patch(buildTemplateSelectionPatch(full, t));
        }
      } catch {
        // ignore — template field mappings unavailable, keep empty
      }
    }
  }, [enableManualModePicker, patch, services, state.file]);

  // -----------------------------------------------------------------------
  // File pick / reset
  // -----------------------------------------------------------------------
  const handleFileSelect = useCallback((f: File | null) => {
    // A geo file always forces parcel mode (geo files are inherently
    // parcel-shaped). For a non-geo file, preserve dataMode ONLY when a
    // template is selected — the one case where dataMode has an
    // authoritative source that a file swap shouldn't stomp. No template +
    // non-geo file resets to null so the manual picker reappears.
    const autoMode = !enableManualModePicker
      ? state.dataMode
      : f && isGeoFile(f.name)
        ? 'parcel'
        : (state.selectedTemplate ? state.dataMode : null);
    parseRequestIdRef.current += 1;
    activePreviewTokenRef.current += 1;
    setParsing(false);
    setParseError(null);
    setUploadError(null);
    setUploadProgress(null);
    setPreviewUiStage('idle');
    patch({
      file: f,
      filename: f?.name ?? '',
      fileSize: f?.size ?? null,
      sourceFormat: null,
      parseResult: null,
      uploadResult: null,
      dataMode: autoMode,
      selectedSheet: null,
      filterMode: 'all',
      hazardLevels: ['extreme'],
    });
    if (draftRunPersistence && state.runId) {
      void services.updateDraftImportRun?.(state.runId, {
        status: 'draft',
        filename: f?.name ?? '',
        fileSize: f?.size,
        fileType: f?.type || undefined,
        blobUrl: null,
        previewJobId: null,
        parseResult: null,
        selectedSheet: null,
        errorMessage: null,
      });
    }
    void services.clearImportPreviewSession?.().catch((err: unknown) => {
      console.error('[ImportRunFlow] Failed to clear preview session:', err);
    });
  }, [draftRunPersistence, enableManualModePicker, patch, services, state.dataMode, state.runId, state.selectedTemplate]);

  // -----------------------------------------------------------------------
  // Persisted-file pick / reset (tenant-web "select previously imported
  // file" mode). A persisted file is already-converted GeoParquet in blob
  // storage, so selection patches state equivalently to a completed upload:
  // upload is skipped (uploadResult.blobUrl is set) and the file goes
  // straight to preview-parse. GeoParquet is parcel-shaped, so dataMode is
  // forced to 'parcel' — same rule isGeoFile applies to raw geo uploads.
  // -----------------------------------------------------------------------
  const handlePersistedFileSelect = useCallback((f: AvailableImportFile | null) => {
    parseRequestIdRef.current += 1;
    activePreviewTokenRef.current += 1;
    setParsing(false);
    setParseError(null);
    setUploadError(null);
    setUploadProgress(null);
    setPreviewUiStage('idle');
    patch({
      file: null,
      filename: f?.filename ?? '',
      fileSize: f?.sizeBytes ?? null,
      sourceFormat: f ? 'geoparquet' : null,
      parseResult: null,
      uploadResult: f ? { runId: '', blobUrl: f.blobUrl } : null,
      dataMode: f
        ? 'parcel'
        : (enableManualModePicker && !state.selectedTemplate ? null : state.dataMode),
      selectedSheet: null,
      filterMode: 'all',
      hazardLevels: ['extreme'],
    });
    if (draftRunPersistence && state.runId) {
      void services.updateDraftImportRun?.(state.runId, {
        status: 'draft',
        filename: f?.filename ?? '',
        fileSize: f?.sizeBytes,
        fileType: f ? 'geoparquet' : undefined,
        blobUrl: f?.blobUrl ?? null,
        previewJobId: null,
        parseResult: null,
        selectedSheet: null,
        errorMessage: null,
      });
    }
    void services.clearImportPreviewSession?.().catch((err: unknown) => {
      console.error('[ImportRunFlow] Failed to clear preview session:', err);
    });
  }, [draftRunPersistence, enableManualModePicker, patch, services, state.dataMode, state.runId, state.selectedTemplate]);

  // -----------------------------------------------------------------------
  // Upload + async preview parse
  // -----------------------------------------------------------------------
  const handleParse = useCallback(async (sheetName?: string) => {
    // A parse needs either a freshly picked File (upload first) or a
    // persisted selection (uploadResult.blobUrl already points at the
    // converted blob — upload is skipped below).
    if (!state.file && !state.uploadResult?.blobUrl) return;
    if (draftRunPersistence && !state.runId) return;

    // Capture locals up-front so async callbacks have a stable view and can
    // compare against activePreviewTokenRef to detect a stale invocation.
    const selectedFile = state.file;
    const previewToken = activePreviewTokenRef.current;
    const draftRunId = state.runId;
    let uploadRunId: string | undefined = state.uploadResult?.runId;
    let effectiveFilename = state.filename || selectedFile?.name || '';
    let effectiveFileSize = state.fileSize ?? selectedFile?.size ?? 0;
    let effectiveSourceFormat = state.sourceFormat ?? undefined;
    let effectiveFileType = state.uploadResult?.fileType ?? state.parseResult?.fileType;
    const selectedTemplateId = state.selectedTemplate?._id;

    const requestId = ++parseRequestIdRef.current;
    setParsing(true);
    setParseError(null);
    try {
      let blobUrl = state.uploadResult?.blobUrl ?? null;
      if (!blobUrl) {
        // Reaching the upload branch implies a freshly picked File — the
        // entry guard above returns early when neither a File nor a blobUrl
        // exists.
        if (!selectedFile) throw new Error('No file selected for upload');
        setPreviewUiStage('uploading');
        if (draftRunPersistence && draftRunId) {
          await services.updateDraftImportRun?.(draftRunId, {
            status: 'uploading',
            filename: selectedFile.name,
            fileSize: selectedFile.size,
            fileType: selectedFile.type || undefined,
          });
        }
        setUploadProgress(0);
        const uploaded = await services.uploadImportFile(
          selectedFile,
          setUploadProgress,
          draftRunPersistence ? draftRunId ?? undefined : undefined,
        );
        if (activePreviewTokenRef.current !== previewToken) return;
        setUploadProgress(null);
        blobUrl = uploaded.blobUrl;
        uploadRunId = uploaded.runId;
        // Use the uploaded filename/fileSize/sourceFormat — the backend may
        // have converted the file (e.g. GeoJSON → GeoParquet). All downstream
        // calls must use the converted values, not the original File's.
        effectiveFilename = uploaded.filename ?? effectiveFilename;
        effectiveFileSize = uploaded.fileSize ?? effectiveFileSize;
        effectiveSourceFormat = uploaded.sourceFormat ?? uploaded.fileType;
        effectiveFileType = uploaded.fileType ?? effectiveFileType;
        patch({
          filename: effectiveFilename,
          fileSize: effectiveFileSize,
          sourceFormat: effectiveSourceFormat ?? null,
          uploadResult: { runId: uploaded.runId, blobUrl: uploaded.blobUrl, fileType: uploaded.fileType },
        });
        if (draftRunPersistence && draftRunId) {
          await services.updateDraftImportRun?.(draftRunId, {
            status: 'draft',
            filename: effectiveFilename,
            fileSize: effectiveFileSize,
            fileType: selectedFile?.type || undefined,
            blobUrl,
          });
        }
      }
      if (!blobUrl) throw new Error('File upload to blob storage failed — no blobUrl returned.');

      const { jobId } = await services.createPreviewJob({
        runId: draftRunPersistence ? draftRunId ?? undefined : undefined,
        blobUrl,
        filename: effectiveFilename,
        fileSize: effectiveFileSize,
        fileType: effectiveFileType,
        sourceFormat: effectiveSourceFormat,
        sheetName,
        sampleSize: 250,
        filterMode: state.filterMode,
        hazardLevels: state.hazardLevels,
      });
      if (activePreviewTokenRef.current !== previewToken) return;

      if (services.saveImportPreviewSession) {
        await services.saveImportPreviewSession({
          _id: 'tenant-import-preview',
          status: 'queued',
          jobId,
          filename: effectiveFilename,
          fileSize: effectiveFileSize,
          fileType: effectiveFileType || undefined,
          blobUrl,
          runId: uploadRunId,
          templateId: selectedTemplateId,
          selectedSheet: sheetName ?? null,
          parseResult: null,
          updatedAt: new Date().toISOString(),
        });
      }
      if (draftRunPersistence && draftRunId) {
        await services.updateDraftImportRun?.(draftRunId, {
          status: 'parsing',
          previewJobId: jobId,
          filename: effectiveFilename,
          fileSize: effectiveFileSize,
          fileType: effectiveFileType,
          blobUrl,
          selectedSheet: sheetName ?? null,
          errorMessage: null,
        });
      }

      setPreviewUiStage('queued');

      const result = await services.pollPreviewJob(jobId, (job) => {
        if (activePreviewTokenRef.current !== previewToken) return;
        setPreviewUiStage(job.status === 'queued' ? 'queued' : 'processing');
        if (services.saveImportPreviewSession) {
          void services.saveImportPreviewSession({
            _id: 'tenant-import-preview',
            status: job.status,
            jobId,
            filename: effectiveFilename,
            fileSize: effectiveFileSize,
            fileType: effectiveFileType || undefined,
            blobUrl,
            runId: uploadRunId,
            templateId: selectedTemplateId,
            selectedSheet: sheetName ?? null,
            parseResult: job.result ?? null,
            updatedAt: job.updatedAt ?? new Date().toISOString(),
          });
        }
        if (draftRunPersistence && draftRunId) {
          void services.updateDraftImportRun?.(draftRunId, {
            status: 'parsing',
            previewJobId: jobId,
            blobUrl,
            selectedSheet: sheetName ?? null,
          });
        }
      });

      if (activePreviewTokenRef.current !== previewToken) return;
      if (requestId !== parseRequestIdRef.current) return;
      patch({
        filename: effectiveFilename,
        fileSize: effectiveFileSize,
        parseResult: result,
        selectedSheet: result.activeSheet ?? null,
      });
      setPreviewUiStage('parsed');

      if (services.saveImportPreviewSession) {
        await services.saveImportPreviewSession({
          _id: 'tenant-import-preview',
          status: 'succeeded',
          jobId,
          filename: effectiveFilename,
          fileSize: effectiveFileSize,
          fileType: effectiveFileType || undefined,
          blobUrl,
          runId: uploadRunId,
          templateId: selectedTemplateId,
          selectedSheet: result.activeSheet ?? null,
          parseResult: result,
          updatedAt: new Date().toISOString(),
        });
      }
      if (draftRunPersistence && draftRunId) {
        await services.updateDraftImportRun?.(draftRunId, {
          status: 'parsed',
          previewJobId: jobId,
          parseResult: result,
          selectedSheet: result.activeSheet ?? null,
          errorMessage: null,
          columnCount: result.columns.length,
          fileType: result.fileType,
        });
      }
    } catch (err: any) {
      if (requestId !== parseRequestIdRef.current) return;
      setParseError(err.message ?? 'Parse failed');
      setPreviewUiStage('error');
      if (draftRunPersistence && draftRunId) {
        await services.updateDraftImportRun?.(draftRunId, {
          status: 'failed',
          errorMessage: err.message ?? 'Parse failed',
        });
      }
    } finally {
      if (requestId !== parseRequestIdRef.current) return;
      setParsing(false);
    }
  }, [draftRunPersistence, patch, services, state]);

  const handleSheetChange = useCallback((sheet: string) => {
    const currentSheet = state.selectedSheet ?? state.parseResult?.activeSheet ?? null;
    if (parsing || !sheet || sheet === currentSheet) return;
    patch({ selectedSheet: sheet });
    void handleParse(sheet);
  }, [handleParse, parsing, patch, state.parseResult?.activeSheet, state.selectedSheet]);

  const handleUpload = useCallback(async () => {
    if (!state.file) return;
    const selectedFile = state.file;
    const previewToken = activePreviewTokenRef.current;
    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);
    try {
      if (draftRunPersistence && state.runId) {
        await services.updateDraftImportRun?.(state.runId, {
          status: 'uploading',
          filename: selectedFile.name,
          fileSize: selectedFile.size,
          fileType: selectedFile.type || undefined,
        });
      }
      const r = await services.uploadImportFile(
        selectedFile,
        setUploadProgress,
        draftRunPersistence ? state.runId ?? undefined : undefined,
      );
      if (activePreviewTokenRef.current !== previewToken) return;
      patch({
        filename: r.filename ?? selectedFile.name,
        fileSize: r.fileSize ?? selectedFile.size,
        sourceFormat: r.sourceFormat ?? r.fileType ?? null,
        uploadResult: { runId: r.runId, blobUrl: r.blobUrl, fileType: r.fileType },
      });
      if (draftRunPersistence && state.runId) {
        await services.updateDraftImportRun?.(state.runId, {
          status: 'draft',
          filename: r.filename ?? selectedFile.name,
          fileSize: r.fileSize ?? selectedFile.size,
          fileType: selectedFile.type || undefined,
          blobUrl: r.blobUrl,
        });
      }
    } catch (err: any) {
      if (activePreviewTokenRef.current === previewToken) {
        setUploadError(err.message ?? 'Upload failed');
      }
      throw err;
    } finally {
      if (activePreviewTokenRef.current === previewToken) {
        setUploading(false);
        setUploadProgress(null);
      }
    }
  }, [draftRunPersistence, patch, services, state.file, state.runId]);

  // -----------------------------------------------------------------------
  // Dry run
  // -----------------------------------------------------------------------
  const handleDryRun = useCallback(async () => {
    if (!state.uploadResult || !state.selectedTemplate) return;
    setDryRunning(true);
    setDryRunError(null);
    try {
      const matchKey = state.fieldMappings.find(m => m.isMatchKey)?.targetField;
      let created: ImportRun;
      try {
        created = await services.createImportRun({
          runId: draftRunPersistence ? state.runId ?? undefined : undefined,
          templateId: state.selectedTemplate._id,
          // Use the uploaded filename (state.filename), NOT state.file?.name.
          // The backend may have converted the file (e.g. GeoJSON →
          // GeoParquet); the converted name drives the sourceFormat fork.
          filename: state.filename || state.file?.name || '',
          blobUrl: state.uploadResult.blobUrl ?? undefined,
          isDryRun: true,
          mode: state.dataMode ?? 'parcel',
          fieldMappings: state.fieldMappings,
          // parcelOwnerUpdate is update-only by contract — force 'upsert' so
          // the run can never carry an insert-capable behavior.
          importBehavior: state.dataMode === 'parcelOwnerUpdate'
            ? 'upsert'
            : state.importBehavior ?? undefined,
          importName: state.importName || undefined,
          importDescription: state.importDescription || undefined,
          matchKeyField: matchKey,
          dataAsOf: state.dataAsOf || undefined,
          fileSize: state.file?.size ?? state.fileSize ?? undefined,
          fileType: state.uploadResult.fileType ?? state.parseResult?.fileType,
          sourceFormat: state.sourceFormat ?? undefined,
          columnCount: state.parseResult?.columns.length,
          selectedSheet: state.selectedSheet,
          // Per-run override for the jurisdiction boundary filter. The
          // backend uses this in preference to the template default.
          boundaryCheckEnabled: state.boundaryCheckEnabled,
          // Spatial filter for GeoParquet sources — kept stable across
          // preview, dry-run, and commit so the same parcel scope applies.
          filterMode: state.filterMode,
          hazardLevels: state.hazardLevels,
        });
      } catch (err) {
        // Draft-persistence hosts may recover from a proxy abort/timeout by
        // polling the known client-generated draft run id — the API may
        // still have accepted the run.
        if (!draftRunPersistence || !state.runId || !isRecoverableCreateImportRunError(err)) throw err;
        created = {
          _id: state.runId,
          templateId: state.selectedTemplate._id,
          filename: state.filename || state.file?.name || '',
          blobUrl: state.uploadResult.blobUrl ?? undefined,
          status: 'dry-run',
          isDryRun: true,
          startedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } as unknown as ImportRun;
      }
      const runId = (created as any)._id ?? (created as any).id ?? '';

      // Expose the run id immediately so the Cancel button can stop an
      // in-flight dry run. The legacy containers only set dryRunId after
      // polling completed, which left Cancel disabled for the entire run —
      // this restores the mid-run cancel the UI advertises. The id is
      // cleared again on failure/cancel so a stale id can never enable a
      // commit.
      patch({ dryRunId: runId });

      // Seed the progress panel immediately so the user sees "Queued" before
      // the first poll round-trips.
      setLiveProgress({
        summary: null,
        phase: 'queued',
        startedAt: (created as any).startedAt ?? new Date().toISOString(),
      });

      // Poll until the processor reaches a terminal state. onProgress
      // forwards the run's summary snapshot + derived phase into
      // liveProgress so ImportProgressIndicator updates live.
      const polledRun = await services.pollRun(runId, 'dryRun', (progress) => {
        setLiveProgress(prev => ({
          ...prev,
          summary: (progress.summary as ProgressSummary | null) ?? prev.summary,
          phase: deriveRunUiPhase(progress, 'dryRun'),
          startedAt: prev.startedAt ?? progress.startedAt,
          stage: progress.stage ?? prev.stage,
          stageDetail: progress.stageDetail ?? prev.stageDetail,
        }));
      });

      if ((polledRun as any).status === 'failed') {
        const errMsg = (polledRun as any).summary?.errors?.[0]?.message ?? 'Dry run failed';
        throw new Error(errMsg);
      }
      if ((polledRun as any).status === 'cancelled') {
        // User hit Cancel mid-run. Clear local state so the Re-run button
        // shows up and the user can adjust mappings then try again.
        setDryRunError(null);
        patch({ dryRunId: null, dryRunSummary: null, dryRunRows: [] });
        setLiveProgress({ summary: null, phase: 'cancelled' });
        return;
      }

      const s = (polledRun as any).summary;
      // Error-only rows for the inline table (hosts without the row
      // explorer). Match-key display uses the first raw-row value.
      const dryRunRows: RunFlowDryRunRow[] = (s?.errors ?? []).slice(0, 250).map((e: any) => ({
        rowNumber: e.row,
        matchKeyValue: e.rawRow ? String(Object.values(e.rawRow)[0] ?? '') : (e.rawValue ?? `row ${e.row}`),
        status: 'error' as const,
        reason: e.message,
      }));

      const dryRunSummary: RunFlowDryRunSummary = s
        ? {
            totalRows: s.totalRows,
            created: s.created,
            updated: s.updated,
            softDeleted: s.softDeleted ?? 0,
            excluded: {
              outOfJurisdiction: s.excluded?.outOfJurisdiction ?? 0,
              missingCoordinates: s.excluded?.missingCoordinates ?? 0,
              duplicateInsertOnly: s.excluded?.duplicateInsertOnly ?? 0,
              missingMatchKey: s.excluded?.missingMatchKey ?? 0,
              noMatch: s.excluded?.noMatch ?? 0,
              staleSource: s.excluded?.staleSource ?? 0,
            },
            failedByKind: {
              transform: s.failedByKind?.transform ?? 0,
              entityBuild: s.failedByKind?.entityBuild ?? 0,
            },
            previewRows: s.previewRows,
            enrichmentCounts: s.enrichmentCounts,
          }
        : {
            totalRows: 0,
            created: 0,
            updated: 0,
            softDeleted: 0,
            excluded: { outOfJurisdiction: 0, missingCoordinates: 0, duplicateInsertOnly: 0, missingMatchKey: 0, noMatch: 0, staleSource: 0 },
            failedByKind: { transform: 0, entityBuild: 0 },
          };

      patch({ dryRunId: runId, dryRunSummary, dryRunRows });
      await services.clearImportPreviewSession?.();
    } catch (err: any) {
      // Clear the dry-run id set when the run was created so a failed run
      // cannot be committed and the Cancel button doesn't stay armed.
      patch({ dryRunId: null });
      setDryRunError(err.message ?? 'Dry run failed');
    } finally {
      setDryRunning(false);
      setCancellingDryRun(false);
    }
  }, [draftRunPersistence, patch, services, state]);

  // Cancel an in-flight dry run. The backend picks up the cancel signal at
  // the next 500-row checkpoint; once the run flips to status='cancelled',
  // pollRun returns and handleDryRun's cancelled branch drives the cleanup.
  const handleCancelDryRun = useCallback(async () => {
    if (!state.dryRunId || cancellingDryRun) return;
    setCancellingDryRun(true);
    try {
      await services.cancelImportRun(state.dryRunId);
      // Don't clear state here — let the poller see the cancelled status and
      // handleDryRun's cancelled branch drive the cleanup. One source of
      // truth for the post-cancel UI state.
    } catch (err: any) {
      setDryRunError(err?.message ?? 'Cancel failed — the run may still be processing.');
      setCancellingDryRun(false);
    }
  }, [cancellingDryRun, services, state.dryRunId]);

  // Reset dry-run state so the user can re-run after adjusting mappings,
  // match key, or import behavior. Does NOT touch uploadResult/parseResult.
  const handleResetDryRun = useCallback(() => {
    setDryRunError(null);
    patch({ dryRunId: null, dryRunSummary: null, dryRunRows: [], proceedWithErrors: false });
    setLiveProgress(RUN_FLOW_LIVE_PROGRESS_EMPTY);
  }, [patch]);

  const handleRerunDryRun = useCallback(() => {
    handleResetDryRun();
    void handleDryRun();
  }, [handleDryRun, handleResetDryRun]);

  // -----------------------------------------------------------------------
  // Commit
  // -----------------------------------------------------------------------
  const handleCommit = useCallback(async () => {
    if (!state.dryRunId) return;
    // Don't declare victory until the commit processor reaches a terminal
    // state: commitImportRun returns immediately (fire-and-forget on the
    // server); pollRun observes the real final status.
    patch({ commitStatus: 'committing', commitError: null });
    // Seed the progress panel with the dry-run totals so the bar renders
    // immediately at 0% fill instead of blank.
    setLiveProgress({
      summary: state.dryRunSummary
        ? {
            totalRows: state.dryRunSummary.totalRows,
            created: 0,
            updated: 0,
            softDeleted: 0,
            excluded: { outOfJurisdiction: 0, missingCoordinates: 0, duplicateInsertOnly: 0, missingMatchKey: 0, noMatch: 0, staleSource: 0 },
            failedByKind: { transform: 0, entityBuild: 0 },
          }
        : null,
      phase: 'queued',
      startedAt: new Date().toISOString(),
    });
    try {
      // Draft-persistence hosts resume gracefully: if the run already
      // completed (or is mid-commit), skip straight to polling instead of
      // double-firing the commit.
      if (draftRunPersistence && services.getImportRun) {
        const currentRun = await services.getImportRun(state.dryRunId).catch(() => null);
        if (currentRun?.status === 'completed') {
          patch({ commitRunId: state.dryRunId, commitStatus: 'completed' });
          const summary = (currentRun as any).summary as ProgressSummary | undefined;
          setLiveProgress(prev => ({ ...prev, summary: summary ?? prev.summary, phase: 'done' }));
          return;
        }
        if (currentRun?.status !== 'committing') {
          try {
            await services.commitImportRun(state.dryRunId);
          } catch (err) {
            if (!isRecoverableCreateImportRunError(err)) throw err;
          }
        }
      } else {
        await services.commitImportRun(state.dryRunId);
      }
      patch({ commitRunId: state.dryRunId });
      const finalRun = await services.pollRun(state.dryRunId, 'commit', (progress) => {
        setLiveProgress(prev => ({
          ...prev,
          summary: (progress.summary as ProgressSummary | null) ?? prev.summary,
          phase: deriveRunUiPhase(progress, 'commit'),
          startedAt: prev.startedAt ?? progress.startedAt,
          stage: progress.stage ?? prev.stage,
          stageDetail: progress.stageDetail ?? prev.stageDetail,
        }));
      });
      if (finalRun.status === 'completed') {
        patch({ commitStatus: 'completed' });
      } else if (finalRun.status === 'cancelled') {
        patch({ commitStatus: 'failed', commitError: 'Commit was cancelled' });
      } else {
        const message =
          (finalRun as any).summary?.errors?.[0]?.message ??
          'Commit failed on the server. Check the run detail for error details.';
        patch({ commitStatus: 'failed', commitError: message });
      }
    } catch (err: any) {
      patch({ commitStatus: 'failed', commitError: err.message ?? 'Commit failed' });
    }
  }, [draftRunPersistence, patch, services, state.dryRunId, state.dryRunSummary]);

  // -----------------------------------------------------------------------
  // Save-as-variant / save-as-new-template (post-commit)
  // -----------------------------------------------------------------------
  const refreshTemplates = useCallback(async () => {
    try {
      const ts = await services.listImportTemplates();
      setTemplates(ts);
    } catch {
      // non-fatal; list catches up on next page load
    }
  }, [services]);

  const handleSaveAsNewVariant = useCallback(async (name: string, defaultVisibility?: string) => {
    const shell = state.selectedTemplate;
    if (!shell) return;
    setSaveVariantBusy(true);
    setSaveVariantError(null);
    try {
      await services.createImportTemplate({
        name,
        description: `Variant of "${shell.name}" created from import run.`,
        dataMode: shell.dataMode,
        importBehavior: state.importBehavior,
        fieldMappings: state.fieldMappings,
        matchKeyField: state.fieldMappings.find(m => m.isMatchKey)?.targetField,
        parentTemplateId: shell._id,
        ...(defaultVisibility
          ? { visibility: ((shell as any).visibility ?? defaultVisibility) as ImportTemplate['visibility'] }
          : {}),
      } as Partial<ImportTemplate> & { name: string });
      await refreshTemplates();
      setSaveVariantDismissed(true);
    } catch (err: any) {
      setSaveVariantError(err?.message ?? 'Could not save variant.');
    } finally {
      setSaveVariantBusy(false);
    }
  }, [refreshTemplates, services, state.fieldMappings, state.importBehavior, state.selectedTemplate]);

  const handleSaveAsNewTemplate = useCallback(async (name: string, description: string, defaultVisibility?: string) => {
    setSaveAsTemplateBusy(true);
    setSaveAsTemplateError(null);
    try {
      await services.createImportTemplate({
        name,
        description: description || `Created from import run "${state.importName}".`,
        dataMode: state.dataMode ?? 'parcel',
        importBehavior: state.importBehavior,
        fieldMappings: state.fieldMappings,
        matchKeyField: state.fieldMappings.find(m => m.isMatchKey)?.targetField,
        ...(defaultVisibility
          ? { visibility: defaultVisibility as ImportTemplate['visibility'] }
          : {}),
      } as Partial<ImportTemplate> & { name: string });
      await refreshTemplates();
      setSaveAsTemplateDismissed(true);
    } catch (err: any) {
      setSaveAsTemplateError(err?.message ?? 'Could not save template.');
    } finally {
      setSaveAsTemplateBusy(false);
    }
  }, [refreshTemplates, services, state.dataMode, state.fieldMappings, state.importBehavior, state.importName]);

  const handleUpdateExistingVariant = useCallback(async (variantId: string) => {
    setSaveVariantBusy(true);
    setSaveVariantError(null);
    try {
      await services.updateImportTemplate(variantId, {
        fieldMappings: state.fieldMappings,
        importBehavior: state.importBehavior,
        matchKeyField: state.fieldMappings.find(m => m.isMatchKey)?.targetField,
      });
      await refreshTemplates();
      setSaveVariantDismissed(true);
    } catch (err: any) {
      setSaveVariantError(err?.message ?? 'Could not update variant.');
    } finally {
      setSaveVariantBusy(false);
    }
  }, [refreshTemplates, services, state.fieldMappings, state.importBehavior]);

  // -----------------------------------------------------------------------
  // Navigation
  // -----------------------------------------------------------------------
  const canAdvance = useCallback((): boolean => {
    if (activeStepId === 'upload') {
      const hasFileContext = !!state.file || !!state.filename;
      // Hosts with the manual picker require a dataMode choice; hosts
      // without it always have one (template-derived or the 'parcel'
      // default), so no extra gate applies.
      const modeOk = !enableManualModePicker || !!state.dataMode;
      // parcelOwnerUpdate (Phase 5a): dataAsOf is the run's freshness
      // claim — the backend rejects creation without a parseable date, so
      // gate here instead of letting the run fail server-side.
      const dataAsOfOk = state.dataMode !== 'parcelOwnerUpdate'
        || (!!state.dataAsOf.trim() && !Number.isNaN(Date.parse(state.dataAsOf)));
      return !!state.importName.trim() && hasFileContext && !!state.parseResult && modeOk && dataAsOfOk;
    }
    if (activeStepId === 'columnMapping' && state.dataMode === 'lookup') {
      // Lookup imports require lookupType, code, and name to be mapped.
      const mapped = new Set(state.fieldMappings.filter(m => m.sourceColumns[0]).map(m => m.targetField));
      return mapped.has('lookupType') && mapped.has('code') && mapped.has('name');
    }
    if (activeStepId === 'columnMapping' && state.dataMode === 'parcelOwnerUpdate') {
      // parcelOwnerUpdate: requires a match key AND at least one mapped
      // ownership.* target — an update run with no ownership mapping would
      // stamp only provenance, which is never the intent.
      const hasMatchKey = state.fieldMappings.some(m => m.isMatchKey && !!m.sourceColumns[0]);
      const hasOwnershipTarget = state.fieldMappings.some(
        m => !!m.sourceColumns[0] && m.targetField.startsWith('ownership.'),
      );
      return hasMatchKey && hasOwnershipTarget;
    }
    if (activeStepId === 'preview') {
      const sum = state.dryRunSummary;
      if (!sum) return false;
      const { failedTotal } = getProgressSummaryTotals(sum);
      return failedTotal === 0 || state.proceedWithErrors;
    }
    if (activeStepId === 'commit') return false;
    return true;
  }, [activeStepId, enableManualModePicker, state]);

  const goNext = useCallback(() => {
    const advance = () => {
      const next = visibleSteps[currentIndex + 1];
      if (next) setActiveStepId(next.id);
    };
    if (activeStepId === 'upload' && state.file && !state.uploadResult) {
      handleUpload()
        .then(() => { if (canGoNext) advance(); })
        .catch(() => { /* uploadError state is already set in handleUpload */ });
      return;
    }
    if (canGoNext) advance();
  }, [activeStepId, canGoNext, currentIndex, handleUpload, state.file, state.uploadResult, visibleSteps]);

  const goBack = useCallback(() => {
    const prev = visibleSteps[currentIndex - 1];
    if (prev) setActiveStepId(prev.id);
  }, [currentIndex, visibleSteps]);

  const nextLabel = useCallback((): string => {
    if (activeStepId === 'upload' && state.file && !state.uploadResult) {
      return uploading ? 'Uploading…' : 'Upload & Next';
    }
    if (!canGoNext) return 'Done';
    return 'Next';
  }, [activeStepId, canGoNext, state.file, state.uploadResult, uploading]);

  return {
    // adapter (exposed for step components that need optional capabilities)
    services,
    // Optional capabilities the host opted into via the services adapter.
    supportsPersistedFiles: !!services.listAvailableImportFiles,
    // state
    state,
    patch,
    // templates
    templates,
    loadingTemplates,
    // upload / parse
    parsing,
    parseError,
    uploading,
    uploadProgress,
    uploadError,
    previewUiStage,
    // dry run
    dryRunning,
    dryRunError,
    cancellingDryRun,
    liveProgress,
    // delete modal (full-sync confirm)
    showDeleteModal,
    setShowDeleteModal,
    // save-as cards
    saveVariantBusy,
    saveVariantError,
    saveVariantDismissed,
    setSaveVariantDismissed,
    saveAsTemplateBusy,
    saveAsTemplateError,
    saveAsTemplateDismissed,
    setSaveAsTemplateDismissed,
    // navigation
    visibleSteps,
    activeStepId,
    setActiveStepId,
    currentIndex,
    canGoBack,
    canGoNext,
    canAdvance,
    goNext,
    goBack,
    nextLabel,
    // handlers
    handleTemplateSelect,
    handleFileSelect,
    handlePersistedFileSelect,
    handleParse,
    handleSheetChange,
    handleUpload,
    handleDryRun,
    handleCancelDryRun,
    handleResetDryRun,
    handleRerunDryRun,
    handleCommit,
    handleSaveAsNewVariant,
    handleSaveAsNewTemplate,
    handleUpdateExistingVariant,
  };
}

export type ImportRunFlow = ReturnType<typeof useImportRunFlow>;
